import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = useCallback(
    (path: string) => {
      if (path === "/") return location.pathname === "/";
      return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (path: string) => {
    setMobileOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const navLinks = [
    { path: "/", label: t("nav.home") },
    { path: "/category/cross-border-business", label: t("nav.categories") },
    { path: "/category/cross-border-business", label: t("nav.articles"), isArticle: true },
    { path: "/about", label: t("nav.about") },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="container-page flex items-center justify-between h-16 md:h-18">
        <Link
          to="/"
          className="flex items-center gap-2 no-underline"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-500">
            <i className="ri-global-line text-background-50 text-lg md:text-xl"></i>
          </div>
          <span className={`font-heading text-lg md:text-xl font-bold whitespace-nowrap ${
            scrolled ? "text-foreground-900" : "text-background-50"
          }`}>
            {t("site.name")}
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <button
              key={link.path + (link.isArticle ? "-article" : "")}
              onClick={() => handleNavClick(link.path)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                isActive(link.path) && !link.isArticle
                  ? scrolled
                    ? "bg-primary-100 text-primary-700"
                    : "bg-background-50/20 text-background-50"
                  : scrolled
                    ? "text-foreground-600 hover:text-foreground-900 hover:bg-background-100"
                    : "text-background-50/80 hover:text-background-50 hover:bg-background-50/10"
              }`}
            >
              {link.label}
            </button>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => handleNavClick("/about")}
            className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
              scrolled
                ? "border border-primary-500 text-primary-600 hover:bg-primary-50"
                : "border border-background-50/50 text-background-50 hover:bg-background-50/10"
            }`}
          >
            {t("cta.contact_us")}
          </button>
          <button
            onClick={() => handleNavClick("/")}
            className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            {t("nav.download")}
          </button>
        </div>

        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer ${
            scrolled ? "text-foreground-900" : "text-background-50"
          }`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="container-page py-4 flex flex-col gap-2">
            {navLinks.map((link) => (
              <button
                key={link.path + (link.isArticle ? "-mobile-article" : "")}
                onClick={() => handleNavClick(link.path)}
                className={`w-full text-left px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  isActive(link.path)
                    ? "bg-primary-50 text-primary-700"
                    : "text-foreground-600 hover:bg-background-100"
                }`}
              >
                {link.label}
              </button>
            ))}
            <div className="flex gap-3 mt-2 pt-3 border-t border-background-200/70">
              <button
                onClick={() => handleNavClick("/about")}
                className="flex-1 px-4 py-3 rounded-full border border-primary-500 text-primary-600 text-sm font-medium whitespace-nowrap text-center cursor-pointer"
              >
                {t("cta.contact_us")}
              </button>
              <button
                onClick={() => handleNavClick("/")}
                className="flex-1 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap text-center cursor-pointer"
              >
                {t("nav.download")}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}